import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  brandColorListToSettings,
  brandColorPositionLabel,
  getBrandColorList,
  MAX_BRAND_COLORS,
} from "../lib/brandColors";
import { cn } from "../lib/utils";
import { BrandKitColorSettings } from "../models/BrandKit";
import { BrandColorPickerPanel } from "./BrandColorShortcuts";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const DEFAULT_NEW_COLOR = "#333333";

interface BrandPaletteEditorProps {
  colors: BrandKitColorSettings | null | undefined;
  onChange: (colors: BrandKitColorSettings) => void;
}

function SortablePaletteSwatch({
  id,
  color,
  index,
  isOpen,
  onOpenChange,
  onColorChange,
  onRemove,
}: {
  id: string;
  color: string;
  index: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onColorChange: (color: string) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const label = brandColorPositionLabel(index);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative"
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "block h-14 w-14 cursor-pointer rounded-xl border border-black/10 shadow-sm transition-transform hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isDragging && "scale-105 cursor-grabbing shadow-md"
            )}
            style={{ backgroundColor: color }}
            aria-label={`Edit ${label.toLowerCase()} color ${color}`}
            title={`${label} · ${color}`}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-auto p-0"
          portal={false}
        >
          <BrandColorPickerPanel
            value={color}
            onChange={onColorChange}
            autoFocus
          />
          <div className="flex items-center gap-0.5 border-t px-2 py-1.5">
            <span className="flex-1 pl-1 text-[11px] text-muted-foreground">
              {label}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              Remove
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <div className="mt-1.5 text-center font-mono text-[10px] tracking-wide text-muted-foreground">
        {color.toUpperCase()}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className={cn(
          "absolute -right-1.5 -top-1.5 z-10 hidden h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:text-destructive group-hover:flex",
          isDragging && "!hidden"
        )}
        aria-label={`Remove ${label.toLowerCase()} color`}
        tabIndex={-1}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * Ordered brand palette editor: drag (or use popover arrows) to reorder,
 * click a swatch to edit, up to MAX_BRAND_COLORS colors. Position carries
 * meaning — the first color is the brand's lead color.
 */
export function BrandPaletteEditor({ colors, onChange }: BrandPaletteEditorProps) {
  const list = useMemo(
    () => getBrandColorList(colors, { dedupe: false }),
    [colors]
  );
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Escape must close only the color popover — never the brand kit dialog.
  // Radix listens for Escape at document level with capture, and the dialog's
  // listener registered first, so the only way to intercept is a window-level
  // capture listener (window capture precedes document capture).
  const anyPopoverOpen = openIndex !== null || addOpen;
  useEffect(() => {
    if (!anyPopoverOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      setOpenIndex(null);
      setAddOpen(false);
    };
    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [anyPopoverOpen]);

  const items = list.map((color, index) => `${index}:${color}`);

  const commit = (next: string[]) => onChange(brandColorListToSettings(next));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    setOpenIndex(null);
    commit(arrayMove(list, oldIndex, newIndex));
  };

  const [draftColor, setDraftColor] = useState(DEFAULT_NEW_COLOR);

  const handleAddApply = (color: string) => {
    if (list.length >= MAX_BRAND_COLORS) return;
    commit([...list, color]);
    setAddOpen(false);
  };

  return (
    <div className="space-y-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-wrap items-start gap-3">
          <SortableContext items={items} strategy={rectSortingStrategy}>
            {list.map((color, index) => (
              <SortablePaletteSwatch
                key={items[index]}
                id={items[index]}
                color={color}
                index={index}
                isOpen={openIndex === index}
                onOpenChange={(open) => setOpenIndex(open ? index : null)}
                onColorChange={(next) =>
                  commit(list.map((c, i) => (i === index ? next : c)))
                }
                onRemove={() => {
                  setOpenIndex(null);
                  commit(list.filter((_, i) => i !== index));
                }}
              />
            ))}
          </SortableContext>
          {list.length < MAX_BRAND_COLORS && (
            <Popover
              open={addOpen}
              onOpenChange={(open) => {
                setAddOpen(open);
                if (open) {
                  setDraftColor(DEFAULT_NEW_COLOR);
                }
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-14 w-14 items-center justify-center rounded-xl border border-dashed border-border text-muted-foreground transition-colors hover:border-ring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Add brand color"
                  title="Add brand color"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={8}
                className="w-auto p-0"
                portal={false}
              >
                <BrandColorPickerPanel
                  value={draftColor}
                  onChange={setDraftColor}
                  autoFocus
                  showActions
                  applyLabel="Add"
                  onApply={handleAddApply}
                  onBack={() => setAddOpen(false)}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </DndContext>
      <p className="text-xs text-muted-foreground">
        {list.length === 0
          ? "Add the colors your brand owns."
          : `Drag to reorder — order tells the AI which colors are most core.${
              list.length >= MAX_BRAND_COLORS ? " 8 of 8 colors." : ""
            }`}
      </p>
    </div>
  );
}
